import React from 'react';

export const PageHeader = ({ title, subtitle, right }) => (
  <div className="flex items-center justify-between mb-5">
    <div>
      <h1 className="text-xl font-semibold">{title}</h1>
      {subtitle && <div className="text-slate-500 text-xs mt-0.5">{subtitle}</div>}
    </div>
    {right}
  </div>
);

export const Card = ({ children, className = '' }) => (
  <div className={`border border-slate-800 rounded-lg bg-slate-900/40 ${className}`}>{children}</div>
);

export const Table = ({ columns, rows, emptyLabel = 'No records', onRowClick, testId }) => (
  <Card>
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid={testId}>
        <thead>
          <tr className="text-left text-[11px] text-slate-500 uppercase tracking-wider border-b border-slate-800">
            {columns.map((c) => <th key={c.key} className="px-4 py-3 font-medium">{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td className="px-4 py-8 text-center text-slate-600" colSpan={columns.length}>{emptyLabel}</td></tr>
          ) : rows.map((r) => (
            <tr key={r.id} onClick={() => onRowClick?.(r)}
                className={`border-b border-slate-800/50 last:border-0 ${onRowClick ? 'hover:bg-slate-800/30 cursor-pointer' : ''}`}>
              {columns.map((c) => <td key={c.key} className="px-4 py-3 text-slate-200">{c.render ? c.render(r) : r[c.key] ?? '—'}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Card>
);

export const Button = ({ children, variant = 'primary', ...rest }) => {
  const cls = variant === 'primary'
    ? 'bg-emerald-500 text-emerald-950 hover:brightness-110'
    : variant === 'ghost'
    ? 'border border-slate-700 text-slate-200 hover:bg-slate-800/50'
    : 'bg-rose-500/20 text-rose-300 border border-rose-500/40 hover:bg-rose-500/30';
  return <button {...rest} className={`px-4 py-2 rounded text-sm font-semibold ${cls} ${rest.className || ''}`}>{children}</button>;
};

export const Input = (props) => (
  <input {...props} className={`w-full bg-slate-950 border border-slate-800 rounded px-3 py-2 text-sm ${props.className || ''}`} />
);

export const Select = ({ options, ...rest }) => (
  <select {...rest} className={`w-full bg-slate-950 border border-slate-800 rounded px-3 py-2 text-sm ${rest.className || ''}`}>
    {options.map((o) => Array.isArray(o) ? <option key={o[0]} value={o[0]}>{o[1]}</option> : <option key={o} value={o}>{o}</option>)}
  </select>
);

export const Label = ({ children, className = '' }) => (
  <label className={`block text-[11px] uppercase tracking-wider text-slate-500 mb-1 ${className}`}>{children}</label>
);

export const Badge = ({ children, tone = 'slate' }) => {
  const tones = {
    slate: 'bg-slate-700/30 text-slate-300 border-slate-600/40',
    emerald: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
    amber: 'bg-amber-500/15 text-amber-300 border-amber-500/40',
    rose: 'bg-rose-500/15 text-rose-300 border-rose-500/40',
    sky: 'bg-sky-500/15 text-sky-300 border-sky-500/40',
  };
  return <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${tones[tone] || tones.slate}`}>{children}</span>;
};

export const Modal = ({ open, onClose, title, children, wide = false }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={onClose} data-testid="liq-modal">
      <div className={`bg-slate-900 border border-slate-800 rounded-lg p-6 w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200" data-testid="liq-modal-close">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
};

export const useAsync = () => {
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const run = async (fn) => {
    setBusy(true); setErr('');
    try { return await fn(); } catch (x) { setErr(x?.response?.data?.detail || 'Error'); throw x; } finally { setBusy(false); }
  };
  return { busy, err, run, setErr };
};
